/**
 * «Posar al dia» (`plans/fitxes/20261012-posar-al-dia.md`) — en Pere, 12-OCT-2026: «taula compacta,
 * tipus excel, pensant en Android», «marco tot jo a la taula», «només última setmana».
 *
 * Un botó a la capçalera del calendari (amb el nombre de marques proposades) que obre un diàleg a
 * pantalla completa al mòbil: una fila per estada de l'última setmana i tres columnes de caselles
 * (Entrat · Marxat · Neta). Una sola crida per marcar-ho tot i un «Desfer» al toast.
 *
 * Amplades a 385 px (diàleg sense marges): Habitació, la resta (~117 px, amb «…» si no hi cap) ·
 * Entrada i Sortida, 56 px · tres columnes de caselles de 52 px. La zona de toc de cada casella és
 * tota la cel·la (52 × 40 px), no el quadrat de 20 px.
 *
 * No hi ha React Hook Form: no és un formulari amb camps i validació, és una graella de caselles amb
 * estat propi; tota la lògica és a `stay-catch-up-logic.ts` (pura i provada) i aquí només es pinta.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { Check, ListChecks, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import type { StayCatchUpRow } from '@guilleumes/types';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useStayCatchUp, useStayCatchUpApply, useStayCatchUpUndo } from '@/hooks/useStayCatchUp';
import { cn } from '@/lib/utils';
import {
  CATCH_UP_COLUMNS,
  COLUMN_LABEL,
  DOORS_UNAVAILABLE_TEXT,
  applySummaryText,
  buildItems,
  cellState,
  checksOf,
  columnState,
  confirmLabel,
  editedAfterApply,
  formatShortDay,
  initialChecks,
  rejectedLines,
  toggleCell,
  toggleColumn,
  undoSummaryText,
  type CatchUpChecks,
  type CatchUpColumn,
  type RejectedLine,
} from './stay-catch-up-logic';

export const CATCH_UP_TITLE = 'Posar al dia';

/** Zona de toc: tota la cel·la, 40 px d'alt, encara que la casella en faci 20. */
const TOUCH_CELL = 'flex h-10 w-full cursor-pointer items-center justify-center touch-manipulation';
/** Capçalera fixa en fer scroll. La ratlla és una ombra: amb `border-collapse` la vora no viatja amb el `sticky`. */
const TH = 'sticky top-0 z-10 bg-gray-50 shadow-[inset_0_-1px_0_#d1d5db]';
const DATE_COL = 'w-14';
const CHECK_COL = 'w-[52px]';

export interface CheckCellProps {
  label: string;
  checked: boolean;
  /** Estat intermedi de la casella «totes». */
  mixed?: boolean;
  disabled?: boolean;
  onChange: (value: boolean) => void;
  className?: string;
  children?: ReactNode;
}

/**
 * Una casella amb tota la cel·la com a zona de toc. El toc el rep el contenidor (un sol camí: la
 * casella de Shadcn porta `pointer-events-none`, i dins d'un `<label>` el seu `onClick` i l'`onChange`
 * de l'input es trepitjarien); el teclat va per l'input de la casella.
 */
export function CheckCell({ label, checked, mixed = false, disabled = false, onChange, className, children }: CheckCellProps) {
  return (
    <div
      className={cn(TOUCH_CELL, disabled && 'cursor-not-allowed', className)}
      onClick={() => {
        if (!disabled) onChange(!checked);
      }}
    >
      {children}
      <span className="pointer-events-none flex">
        <Checkbox
          aria-label={label}
          title={label}
          checked={checked}
          aria-checked={mixed ? 'mixed' : checked}
          disabled={disabled}
          onCheckedChange={onChange}
          // Estat intermedi: l'atribut natiu (lectors de pantalla) i una ratlla a la casella.
          ref={(input) => {
            if (input) input.indeterminate = mixed;
          }}
          className={cn(
            'h-5 w-5 bg-white',
            mixed &&
              "border-blue-600 bg-blue-100 after:block after:h-0.5 after:w-2.5 after:rounded after:bg-blue-700 after:content-['']",
          )}
        />
      </span>
    </div>
  );
}

export interface StayCatchUpTableProps {
  rows: readonly StayCatchUpRow[];
  checks: CatchUpChecks;
  disabled?: boolean;
  onToggle: (row: StayCatchUpRow, column: CatchUpColumn, value: boolean) => void;
  onToggleAll: (column: CatchUpColumn, value: boolean) => void;
}

/** La taula, sense hooks: els tests la pinten amb `renderToStaticMarkup`. */
export function StayCatchUpTable({ rows, checks, disabled, onToggle, onToggleAll }: StayCatchUpTableProps) {
  return (
    <table className="w-full table-fixed border-collapse text-[13px]" data-testid="stay-catch-up-table">
      <thead>
        <tr className="text-[11px] font-semibold uppercase tracking-tight text-gray-600">
          <th scope="col" className={cn(TH, 'py-1 pl-2 pr-1 text-left')}>
            Habitació
          </th>
          <th scope="col" className={cn(TH, 'px-0.5 py-1 text-left', DATE_COL)}>
            Entrada
          </th>
          <th scope="col" className={cn(TH, 'px-0.5 py-1 text-left', DATE_COL)}>
            Sortida
          </th>
          {CATCH_UP_COLUMNS.map((column) => {
            const state = columnState(checks, rows, column);
            const label = `Totes: ${COLUMN_LABEL[column]}`;
            return (
              <th
                key={column}
                scope="col"
                data-column-all={column}
                data-state={state}
                className={cn(TH, 'border-l p-0 text-center', CHECK_COL)}
              >
                <CheckCell
                  label={label}
                  checked={state === 'all'}
                  mixed={state === 'some'}
                  disabled={disabled || state === 'empty'}
                  onChange={(value) => onToggleAll(column, value)}
                  className="h-12 flex-col gap-0.5"
                >
                  <span className="leading-none">{COLUMN_LABEL[column]}</span>
                </CheckCell>
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const rowChecks = checksOf(checks, row);
          return (
            <tr key={row.reservationId} className="border-b even:bg-gray-50/60" data-row={row.reservationId}>
              <th scope="row" className="truncate py-0 pl-2 pr-1 text-left font-semibold text-gray-900" title={row.roomName}>
                {row.roomName}
              </th>
              <td className="whitespace-nowrap px-0.5 py-0 tabular-nums text-gray-700">{formatShortDay(row.checkIn)}</td>
              <td className="whitespace-nowrap px-0.5 py-0 tabular-nums text-gray-700">{formatShortDay(row.checkOut)}</td>
              {CATCH_UP_COLUMNS.map((column) => {
                const state = cellState(row, column);
                const label = `${row.roomName}: ${COLUMN_LABEL[column]}`;
                return (
                  <td key={column} className="border-l p-0 text-center" data-cell={column} data-state={state}>
                    {state === 'done' ? (
                      <span className="flex h-10 items-center justify-center" title={`${label} (ja consta)`}>
                        <Check className="h-5 w-5 text-emerald-600" aria-hidden="true" />
                        <span className="sr-only">{label}: ja consta</span>
                      </span>
                    ) : state === 'na' ? (
                      <span className="flex h-10 items-center justify-center text-gray-300" aria-hidden="true">
                        ·
                      </span>
                    ) : (
                      <CheckCell
                        label={label}
                        checked={rowChecks[column]}
                        disabled={disabled}
                        onChange={(value) => onToggle(row, column, value)}
                      />
                    )}
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export interface StayCatchUpBodyProps {
  rows: readonly StayCatchUpRow[];
  checks: CatchUpChecks;
  loading: boolean;
  error: string | null;
  pending: boolean;
  rejected: readonly RejectedLine[];
  /** El backend no ha pogut llegir les portes: s'avisa, i no ve cap «Entrat» proposat. */
  doorsUnavailable?: boolean;
  onToggle: StayCatchUpTableProps['onToggle'];
  onToggleAll: StayCatchUpTableProps['onToggleAll'];
  onCancel: () => void;
  onConfirm: () => void;
}

/** Cos del diàleg (taula amb scroll i peu fix), sense hooks. */
export function StayCatchUpBody({
  rows,
  checks,
  loading,
  error,
  pending,
  rejected,
  doorsUnavailable = false,
  onToggle,
  onToggleAll,
  onCancel,
  onConfirm,
}: StayCatchUpBodyProps) {
  const count = buildItems(checks, rows).length;
  return (
    <>
      {doorsUnavailable && (
        <p
          role="status"
          data-testid="stay-catch-up-doors-unavailable"
          className="shrink-0 border-b border-amber-200 bg-amber-50 px-3 py-1.5 text-xs text-amber-900"
        >
          {DOORS_UNAVAILABLE_TEXT}
        </p>
      )}
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain" data-testid="stay-catch-up-scroll">
        {loading ? (
          <p className="flex items-center justify-center gap-2 p-6 text-sm text-gray-500" role="status">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            Carregant…
          </p>
        ) : error ? (
          <p className="p-4 text-sm text-red-700" role="alert">
            No s&apos;ha pogut carregar: {error}
          </p>
        ) : rows.length === 0 ? (
          <p className="p-6 text-center text-sm text-gray-500" data-testid="stay-catch-up-empty">
            Tot al dia: no hi ha res per marcar de l&apos;última setmana.
          </p>
        ) : (
          <StayCatchUpTable
            rows={rows}
            checks={checks}
            disabled={pending}
            onToggle={onToggle}
            onToggleAll={onToggleAll}
          />
        )}
      </div>

      {rejected.length > 0 && (
        <div
          role="alert"
          data-testid="stay-catch-up-rejected"
          className="max-h-32 shrink-0 overflow-y-auto border-t border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800"
        >
          <p className="font-semibold">No s&apos;han pogut marcar:</p>
          <ul>
            {rejected.map((line) => (
              <li key={line.key}>
                {line.what}: {line.why}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex shrink-0 gap-2 border-t bg-white p-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
        <Button type="button" variant="outline" className="h-11 flex-1" onClick={onCancel} disabled={pending}>
          Cancel·lar
        </Button>
        <Button
          type="button"
          className="h-11 flex-[2]"
          onClick={onConfirm}
          disabled={pending || count === 0}
          data-testid="stay-catch-up-confirm"
        >
          {pending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
          {confirmLabel(count)}
        </Button>
      </div>
    </>
  );
}

export interface StayCatchUpTriggerProps {
  /** Marques proposades: el comptador petit. 0 o sense dades, no es pinta. */
  suggested: number;
  onClick: () => void;
}

/** El botó de la capçalera del calendari, sense hooks. */
export function StayCatchUpTrigger({ suggested, onClick }: StayCatchUpTriggerProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="stay-catch-up-trigger"
      aria-label={suggested > 0 ? `${CATCH_UP_TITLE}: ${suggested} per marcar` : CATCH_UP_TITLE}
      className={cn(
        'mr-1 flex h-7 flex-shrink-0 items-center gap-1 whitespace-nowrap rounded-full border px-3 text-xs font-medium',
        'border-emerald-300 bg-white text-emerald-800 transition-colors hover:bg-emerald-50 touch-manipulation',
      )}
    >
      <ListChecks className="h-3.5 w-3.5" aria-hidden="true" />
      {CATCH_UP_TITLE}
      {suggested > 0 && (
        <span
          data-testid="stay-catch-up-count"
          className="rounded-full bg-emerald-600 px-1.5 text-[10px] font-semibold leading-4 text-white"
        >
          {suggested}
        </span>
      )}
    </button>
  );
}

const NO_ROWS: readonly StayCatchUpRow[] = [];

/** Botó + diàleg. Va a la capçalera del calendari, només per a qui pot marcar (ADMIN i STAFF). */
export function StayCatchUp() {
  const [open, setOpen] = useState(false);
  /** Caselles tocades a mà; `null` = l'estat inicial que surt de la proposta. */
  const [edited, setEdited] = useState<CatchUpChecks | null>(null);
  const [rejected, setRejected] = useState<readonly RejectedLine[]>([]);

  const query = useStayCatchUp();
  const applyMutation = useStayCatchUpApply();
  const undoMutation = useStayCatchUpUndo();

  const rows = query.data?.rows ?? NO_ROWS;
  const doorsUnavailable = query.data?.doorsUnavailable === true;
  const proposed = useMemo(() => initialChecks(rows, doorsUnavailable), [rows, doorsUnavailable]);
  const checks = edited ?? proposed;

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    setEdited(null);
    setRejected([]);
    if (next) void query.refetch();
  };

  const handleConfirm = () => {
    const items = buildItems(checks, rows);
    if (items.length === 0) return;
    applyMutation.mutate(items, {
      onSuccess: (result) => {
        const undo = result.undo;
        const text = applySummaryText(result.summary);
        const action =
          undo.length > 0
            ? {
                label: 'Desfer',
                onClick: () =>
                  undoMutation.mutate(undo, {
                    onSuccess: (undone) => toast.success(undoSummaryText(undone.summary)),
                  }),
              }
            : undefined;
        setEdited(editedAfterApply(edited, result.summary));
        if (result.summary.rejected > 0) {
          // Es queda obert ensenyant quins i per què.
          setRejected(rejectedLines(result.results, rows));
          toast.warning(text, { action, duration: 10_000 });
        } else {
          setRejected([]);
          setOpen(false);
          toast.success(text, { action, duration: 10_000 });
        }
      },
    });
  };

  return (
    <>
      <StayCatchUpTrigger suggested={query.data?.suggested ?? 0} onClick={() => handleOpenChange(true)} />
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent
          data-testid="stay-catch-up-dialog"
          className={cn(
            // Mòbil: pantalla completa. A partir de `sm`, un diàleg centrat.
            'flex h-[100dvh] max-h-[100dvh] w-screen max-w-none flex-col gap-0 rounded-none border-0 p-0',
            'sm:h-auto sm:max-h-[85vh] sm:w-full sm:max-w-lg sm:rounded-lg sm:border',
          )}
        >
          <DialogHeader className="shrink-0 gap-0.5 border-b px-3 py-2 pr-10 text-left">
            <DialogTitle className="text-base">{CATCH_UP_TITLE}</DialogTitle>
            <DialogDescription className="text-xs">
              Última setmana. Marca qui ha entrat, qui ha marxat i què és net.
            </DialogDescription>
          </DialogHeader>
          <StayCatchUpBody
            rows={rows}
            checks={checks}
            loading={query.isLoading}
            error={query.isError ? (query.error instanceof Error ? query.error.message : 'error') : null}
            pending={applyMutation.isPending}
            rejected={rejected}
            doorsUnavailable={doorsUnavailable}
            onToggle={(row, column, value) => {
              setEdited(toggleCell(checks, row, column, value));
            }}
            onToggleAll={(column, value) => {
              setEdited(toggleColumn(checks, rows, column, value));
            }}
            onCancel={() => handleOpenChange(false)}
            onConfirm={handleConfirm}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}

export default StayCatchUp;
